import { CEFR_LEVELS, EXAM_PASS_PCT, type CefrLevel } from "@rmnlng/shared";
import { ArrowRight, BookOpen, CheckCircle2, Home, Lightbulb, ListChecks, RefreshCw, XCircle } from "lucide-react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { ExamSectionScore, ExamSubmitResponse } from "@/lib/exam";
import { useT, type I18nKey } from "@/lib/i18n";

// `/levels/:level/exam/result` — final screen after submit. Receives the
// full ExamSubmitResponse via location.state. Pass = green hero +
// "Continue to {next}" CTA. Fail = red hero + "Back to Levels" + the
// 24h cooldown reminder.
//
// Per-section breakdown bars (red <80% / green ≥80%) follow the design
// hi-fi treatment. Weak-area "practice tip" callout is deferred to the
// follow-up polish pass.
export function ExamResultsPage() {
  const { level } = useParams<{ level: string }>();
  const location = useLocation();
  const t = useT();

  const isValid = CEFR_LEVELS.includes(level as CefrLevel);
  const targetLevel = level as CefrLevel;

  const result = (location.state as { result?: ExamSubmitResponse } | null)?.result;
  if (!isValid) return <Navigate to="/levels" replace />;
  if (!result) {
    // No submit response in state — refresh / direct nav. Bounce back
    // to the level page so the user can see their advanced state.
    return <Navigate to={`/levels/${targetLevel}`} replace />;
  }

  const { attempt, currentLevel } = result;
  const passed = attempt.passed;
  const minutes = Math.floor(attempt.durationSeconds / 60);
  const seconds = attempt.durationSeconds % 60;
  const timeFmt = `${minutes}:${String(seconds).padStart(2, "0")}`;

  return (
    <div className="flex min-h-[calc(100dvh-40px)] flex-col gap-4">
      {/* Hero */}
      <div
        className="card-elevated rounded-[var(--r-lg)] p-5 text-center"
        style={{
          background: passed ? "var(--good-soft)" : "var(--again-soft)",
        }}
      >
        <div
          className="mx-auto mb-2 flex size-16 items-center justify-center rounded-full"
          style={{
            background: passed ? "var(--good)" : "var(--again)",
            color: "white",
          }}
        >
          {passed ? <CheckCircle2 className="size-8" /> : <XCircle className="size-8" />}
        </div>
        <div
          className="text-[20px] font-bold tracking-[-0.02em]"
          style={{ color: passed ? "var(--good)" : "var(--again)" }}
        >
          {passed ? t("exam.result_pass_title", { level: targetLevel }) : t("exam.result_fail_title")}
        </div>
        <div className="mt-1 text-[13px] text-text-2">
          {passed
            ? t("exam.result_pass_sub", { level: targetLevel })
            : t("exam.result_fail_sub", { pass: EXAM_PASS_PCT })}
        </div>
        {/* Score + time strip */}
        <div className="mt-3 flex items-center justify-center gap-4 text-[12px] text-text-3">
          <div>
            <span className="font-mono text-[20px] font-bold tabular-nums" style={{ color: "var(--text)" }}>
              {attempt.score}%
            </span>
          </div>
          <div className="font-mono tabular-nums">{timeFmt}</div>
        </div>
      </div>

      {/* Per-section breakdown */}
      <div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
          {t("exam.result_breakdown_title")}
        </div>
        <div className="flex flex-col gap-1.5 rounded-[var(--r-md)] border border-border bg-surface p-3">
          {attempt.sections.map((s) => {
            const isFull = s.pct >= EXAM_PASS_PCT;
            return (
              <div key={s.section} className="flex flex-col gap-0.5">
                <div className="flex items-baseline justify-between text-[12px]">
                  <span className="text-text-2">{t(sectionLabelKey(s.section))}</span>
                  <span className="font-mono text-text-3 tabular-nums">
                    {s.correct}/{s.total} · {s.pct}%
                  </span>
                </div>
                <div className="progress-track" style={{ height: 4 }}>
                  <div
                    className="fill"
                    style={{
                      width: `${s.pct}%`,
                      background: isFull ? "var(--good)" : "var(--again)",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Weak-area tip — only when there's a real signal (at least
          one below-pass section) AND the user can act on it (i.e.,
          when not passed; if they passed, the level moves on and the
          weak section is now "history"). */}
      <WeakAreaTip sections={attempt.sections} passed={passed} />

      {/* CTAs at the bottom — different per pass/fail. The
          "Review answers" link is always available so the user can
          revisit their work whether they passed or not — useful on
          fail to see what tripped them, useful on pass to confirm
          their understanding. */}
      <div className="mt-auto flex flex-col gap-2 pt-2">
        {passed ? (
          <Button asChild size="block">
            <Link to={`/levels/${currentLevel}`}>
              <ArrowRight className="size-4" />
              {t("exam.result_cta_continue", { level: currentLevel })}
            </Link>
          </Button>
        ) : (
          <Button asChild variant="secondary" size="block">
            <Link to={`/levels/${targetLevel}`}>
              <RefreshCw className="size-4" />
              {t("exam.result_cta_retry_later")}
            </Link>
          </Button>
        )}
        <Button asChild variant="secondary" size="block">
          <Link to={`/levels/${targetLevel}/exam/result/${attempt.id}/review`}>
            <ListChecks className="size-4" />
            {t("exam.result_cta_review")}
          </Link>
        </Button>
        <Button asChild variant="ghost" size="block">
          <Link to="/">
            <Home className="size-4" />
            {t("exam.result_cta_home")}
          </Link>
        </Button>
      </div>
    </div>
  );
}

function sectionLabelKey(section: string): I18nKey {
  return `exam.section_${section}` as I18nKey;
}

// Pick the lowest-scoring section among those below the pass mark,
// then suggest a concrete next step. Vocab → /words list, Grammar →
// /grammar list. Skipped entirely on pass (the user advanced; the
// weak section is no longer the binding constraint).
function WeakAreaTip({ sections, passed }: { sections: ExamSectionScore[]; passed: boolean }) {
  const t = useT();
  if (passed) return null;
  const weakest = sections
    .filter((s) => s.pct < EXAM_PASS_PCT)
    // Lowest pct first; ties broken by smallest section count (focus
    // on smaller sections where each question matters more).
    .toSorted((a, b) => (a.pct === b.pct ? a.total - b.total : a.pct - b.pct))[0];
  if (!weakest) return null;

  const isVocab = weakest.section === "vocab_mcq" || weakest.section === "vocab_type";
  const target = isVocab ? "/words" : "/grammar";
  const ctaKey: I18nKey = isVocab ? "exam.weak_area_cta_vocab" : "exam.weak_area_cta_grammar";

  return (
    <Link
      to={target}
      className="flex items-start gap-3 rounded-[var(--r-md)] px-3.5 py-3 text-[12px] transition-colors hover:bg-[color:var(--surface-2)]"
      style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
    >
      <Lightbulb className="mt-0.5 size-4 shrink-0" />
      <div className="flex-1">
        <div className="font-semibold">{t("exam.weak_area_title")}</div>
        <div className="mt-0.5 text-text-2">
          {t("exam.weak_area_body", { section: t(sectionLabelKey(weakest.section)), pct: weakest.pct })}
        </div>
      </div>
      <span className="mt-0.5 inline-flex items-center gap-0.5 text-[11px] font-medium">
        <BookOpen className="size-3" />
        {t(ctaKey)}
      </span>
    </Link>
  );
}
